import type { PlaylistItem } from '@jwp/ott-common/types/playlist';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { createLiveEventMetadata, createVideoFormatMetadata, createVideoMetadata } from '@jwp/ott-common/src/utils/metadata';
import { shallow } from '@jwp/ott-common/src/utils/compare';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import { mediaURL } from '@jwp/ott-common/src/utils/urlFormatting';
import usePlaylist from '@jwp/ott-hooks-react/src/usePlaylist';
import useEntitlement from '@jwp/ott-hooks-react/src/useEntitlement';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import { isPlayableLiveEvent } from '@jwp/ott-common/src/utils/liveEvent';

export default function useLiveEventData(data: PlaylistItem, id: string, feedId: string | null) {
  const { t, i18n } = useTranslation(['video', 'common']);

  // Config
  const { config, accessModel } = useConfigStore(({ config, accessModel }) => ({ config, accessModel }), shallow);
  const { features, custom } = config;
  const isFavoritesEnabled: boolean = Boolean(features?.favoritesList);
  const isSharingEnabled: boolean = Boolean(!custom?.disableShare);

  // Media
  const { isLoading: isPlaylistLoading, data: playlist } = usePlaylist(features?.recommendationsPlaylist || '', { related_media_id: id });
  const nextItem = useMemo(() => {
    if (!id || !playlist) return;

    const index = playlist.playlist.findIndex(({ mediaid }) => mediaid === id);
    const nextItem = playlist.playlist[index + 1];

    return nextItem;
  }, [id, playlist]);

  // User, entitlement
  const { isEntitled, mediaOffers } = useEntitlement(data);
  const { isLoggedIn, hasSubscription, passes } = useAccountStore(
    ({ user, subscription, passes }) => ({ isLoggedIn: !!user, hasSubscription: !!subscription, passes }),
    shallow,
  );
  const hasMediaOffers = !!mediaOffers.length;

  // UI
  const getCardUrl = (item: PlaylistItem) => mediaURL({ id: item.mediaid, title: item.title, playlistId: config.features?.recommendationsPlaylist });
  const movieURL = mediaURL({ id: data.mediaid, title: data.title, playlistId: feedId, play: false });
  const playUrl = mediaURL({ id: data.mediaid, title: data.title, playlistId: feedId, play: true });
  const primaryMetadata = [
    ...createVideoMetadata(
      { ...data, rating: '', pubdate: 0 },
      {
        hoursAbbreviation: t('common:abbreviation.hours'),
        minutesAbbreviation: t('common:abbreviation.minutes'),
      },
    ),
  ];
  const liveEventMetadata = createLiveEventMetadata(data.scheduledStart, data.scheduledEnd, i18n.language);

  const videoFormats = createVideoFormatMetadata(data);
  const videoRating = [data?.rating, data?.ratingComponents].filter(Boolean);

  return {
    passes,
    isPlayable: isPlayableLiveEvent(data),
    isLoggedIn,
    hasSubscription,
    playlist,
    isPlaylistLoading,
    hasMediaOffers,
    mediaOffers,
    movieURL,
    playUrl,
    nextItem,
    accessModel,
    isFavoritesEnabled,
    isSharingEnabled,
    isEntitled,
    primaryMetadata,
    liveEventMetadata,
    videoFormats,
    videoRating,
    getCardUrl,
  };
}
